import { cn } from '@/lib/utils'
import type { PanelArtProps } from './panel-art-props'

/**
 * Donut by Category panel art: a four slice ring with a matching key at right,
 * each entry a colour dot and a caption bar.
 *
 * The ring is four stroked arcs rather than a clipped or masked shape: both of
 * those need an id, and ids across a set of thirty-one inline illustrations are a
 * collision waiting to happen. A small angular gap separates the slices, so the
 * butt caps never need to meet exactly.
 */

/** [arc path, stroke] in clockwise order from twelve o'clock. */
const SLICES: [string, string][] = [
  ['M46.98 22.02 A28 28 0 0 1 65.87 69.73', 'hsl(var(--primary) / 0.8)'],
  ['M64.44 71.07 A28 28 0 0 1 23.94 67.24', 'hsl(var(--positive) / 0.7)'],
  ['M22.79 65.66 A28 28 0 0 1 22.79 34.34', 'hsl(var(--negative) / 0.7)'],
  ['M23.94 32.76 A28 28 0 0 1 45.02 22.02', 'hsl(var(--muted-foreground) / 0.55)'],
]

/** [dot centre y, dot fill, caption width] */
const LEGEND: [number, string, number][] = [
  [24, 'hsl(var(--primary) / 0.8)', 44],
  [42, 'hsl(var(--positive) / 0.7)', 36],
  [60, 'hsl(var(--negative) / 0.7)', 40],
  [78, 'hsl(var(--muted-foreground) / 0.55)', 30],
]

export function DonutCategoryArt({ className }: PanelArtProps) {
  return (
    <svg
      viewBox="0 0 160 100"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
      className={cn('h-full w-full', className)}
    >
      {SLICES.map(([d, stroke]) => (
        <path key={d} d={d} fill="none" stroke={stroke} strokeWidth="12" strokeLinecap="butt" />
      ))}
      {LEGEND.map(([cy, fill, width]) => (
        <g key={cy}>
          <circle cx={94} cy={cy} r={4} fill={fill} />
          <rect
            x={104}
            y={cy - 2.5}
            width={width}
            height={5}
            rx={2}
            fill="hsl(var(--foreground) / 0.16)"
          />
        </g>
      ))}
    </svg>
  )
}
